import { useSeoMeta, useHead } from '@unhead/react'
import { useEffect, useState } from 'react'
import { ExportButtons } from '@/components/ExportButtons'
import { ExportData } from '@/utils/exportUtils'

function FencePostCalculator() {
  useSeoMeta({
    title: 'Fence Post Concrete Calculator | How Many Bags Per Post',
    description: 'Calculate exactly how much concrete you need for fence post holes. Enter post size, hole depth, and get instant bag estimates for 40lb, 60lb, and 80lb bags.',
    ogTitle: 'Fence Post Concrete Calculator | How Many Bags Per Post',
    ogDescription: 'Calculate exactly how much concrete you need for fence post holes. Instant bag estimates for all sizes.',
    ogUrl: 'https://www.concrete-calculator.com/fence-post-calculator/',
    ogType: 'website',
    twitterCard: 'summary_large_image'
  })

  useHead({
    link: [
      { rel: 'canonical', href: 'https://www.concrete-calculator.com/fence-post-calculator/' }
    ]
  })

  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Calculate Concrete for Fence Posts",
      "description": "Step-by-step guide to calculating concrete needed for fence post installation",
      "step": [
        {
          "@type": "HowToStep",
          "position": 1,
          "name": "Measure Post Hole",
          "text": "Measure the diameter and depth of your fence post hole in inches"
        },
        {
          "@type": "HowToStep",
          "position": 2,
          "name": "Enter Dimensions",
          "text": "Input hole diameter and depth into the calculator"
        },
        {
          "@type": "HowToStep",
          "position": 3,
          "name": "Get Results",
          "text": "Calculator shows cubic feet, cubic yards, and number of 80lb bags needed"
        }
      ],
      "tool": {
        "@type": "HowToTool",
        "name": "Fence Post Concrete Calculator"
      }
    })
    document.head.appendChild(script)
    return () => {
      document.head.removeChild(script)
    }
  }, [])

  const [diameter, setDiameter] = useState('')
  const [depth, setDepth] = useState('')
  const [numPosts, setNumPosts] = useState('1')
  const [results, setResults] = useState<{
    cubicFeetPerPost: number
    totalCubicFeet: number
    cubicYards: number
    bags80lb: number
  } | null>(null)

  const calculate = () => {
    const d = parseFloat(diameter)
    const h = parseFloat(depth)
    const posts = parseFloat(numPosts)

    if (isNaN(d) || isNaN(h) || isNaN(posts) || d <= 0 || h <= 0 || posts <= 0) {
      return
    }

    // Convert inches to feet
    const radiusFeet = (d / 2) / 12
    const heightFeet = h / 12

    // Volume = π × r² × h
    const volumePerPost = Math.PI * radiusFeet * radiusFeet * heightFeet
    const totalVolume = volumePerPost * posts
    const cubicYards = totalVolume / 27
    const bags80 = Math.ceil(totalVolume / 0.6)

    setResults({
      cubicFeetPerPost: volumePerPost,
      totalCubicFeet: totalVolume,
      cubicYards: cubicYards,
      bags80lb: bags80
    })
  }

  const getExportData = (): ExportData => ({
    title: 'Fence Post Concrete Calculator Results',
    inputs: [
      { label: 'Hole Diameter', value: `${diameter} inches` },
      { label: 'Hole Depth', value: `${depth} inches` },
      { label: 'Number of Posts', value: numPosts }
    ],
    results: [
      { label: 'Per Post', value: `${results?.cubicFeetPerPost.toFixed(2)} ft³` },
      { label: 'Total Volume', value: `${results?.totalCubicFeet.toFixed(2)} ft³` },
      { label: 'Cubic Yards', value: `${results?.cubicYards.toFixed(2)} yd³` },
      { label: '80 lb Bags Needed', value: `${results?.bags80lb} bags` }
    ]
  })

  const reset = () => {
    setDiameter('')
    setDepth('')
    setNumPosts('1')
    setResults(null)
  }

  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 style={{ fontSize: '2rem', color: '#4A4A4A', marginBottom: '1rem' }}>
          Fence Post Concrete Calculator
        </h1>
        
        <nav aria-label="Calculator Tools" className="mb-8">
          <a href="/" className="text-[#FF8C00] hover:underline">← Back to Home</a>
        </nav>

        {/* Calculator */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6 md:p-8 mb-12 shadow-sm">
          <h2 className="text-xl font-bold text-[#4A4A4A] mb-6">Calculate Concrete for Post Holes</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label htmlFor="diameter" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Hole Diameter (inches)
              </label>
              <input
                id="diameter"
                type="number"
                value={diameter}
                onChange={(e) => setDiameter(e.target.value)}
                placeholder="e.g., 12"
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                min="0"
                step="1"
              />
            </div>

            <div>
              <label htmlFor="depth" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Hole Depth (inches)
              </label>
              <input
                id="depth"
                type="number"
                value={depth}
                onChange={(e) => setDepth(e.target.value)}
                placeholder="e.g., 36"
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                min="0"
                step="1"
              />
            </div>

            <div>
              <label htmlFor="numPosts" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Number of Posts
              </label>
              <input
                id="numPosts"
                type="number"
                value={numPosts}
                onChange={(e) => setNumPosts(e.target.value)}
                placeholder="1"
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                min="1"
                step="1"
              />
            </div>
          </div>

          <div className="flex gap-3 mb-6">
            <button
              onClick={calculate}
              className="flex-1 bg-[#FF8C00] text-white font-bold py-3 px-6 rounded-md hover:bg-[#E67E00] transition-colors text-lg"
            >
              Calculate
            </button>
            <button
              onClick={reset}
              className="flex-1 bg-gray-500 text-white font-bold py-3 px-6 rounded-md hover:bg-gray-600 transition-colors text-lg"
            >
              Reset
            </button>
          </div>

          {results && (
            <div className="bg-gray-50 border-2 border-[#FF8C00] rounded-lg p-6">
              <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
                <h3 className="text-xl font-bold text-[#4A4A4A]">Results</h3>
                <ExportButtons data={getExportData()} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Per Post</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{results.cubicFeetPerPost.toFixed(2)} ft³</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Total Volume</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{results.totalCubicFeet.toFixed(2)} ft³</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Cubic Yards</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{results.cubicYards.toFixed(2)} yd³</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">80 lb Bags Needed</p>
                  <p className="text-2xl font-bold text-[#FF8C00]">{results.bags80lb} bags</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 text-sm text-gray-600">
            <p className="mb-2">
              <strong>How to use:</strong> Enter the diameter and depth of your post hole in inches, and the number of posts. Common fence post holes are 12 inches in diameter and 36 inches deep.
            </p>
            <p>
              <strong>Pro Tip:</strong> Dig holes at least 1/3 the height of the above-ground post for stability.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <section aria-labelledby="faq-heading" className="mt-12">
          <h2 id="faq-heading" className="text-2xl font-bold text-[#4A4A4A] mb-4">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                How deep should fence post holes be?
              </h3>
              <p className="text-gray-700">
                Fence post holes should be at least 1/3 the height of the above-ground post. For a 6-foot tall fence, dig holes 24-36 inches deep. In cold climates, dig below the frost line.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                How much concrete per fence post?
              </h3>
              <p className="text-gray-700">
                A standard 12-inch diameter × 36-inch deep post hole requires approximately 2.3 cubic feet of concrete, which equals about 4 bags of 80lb concrete mix.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default FencePostCalculator
